import { Avatar, AvatarFallback, AvatarImage } from "@choose/ui/avatar";
import type { TFunction } from "i18next";
import { UserCircleIcon, SunIcon, MoonIcon, MonitorIcon } from "lucide-react";
import { matchSorter } from "match-sorter";
import { useState } from "react";

import type { InternalUsersQuery } from "~/graphql/graphql.gen";
import { sortRole } from "~/stores/roles-store";
import type { TicketDetail } from "~/stores/tickets-store";

export const getThemeOptions = (t: TFunction) => [
  {
    value: "light",
    label: t("settings.theme.light.label"),
    description: t("settings.theme.light.description"),
    icon: SunIcon,
    illustration: () => (
      <div className="h-[100px] rounded-xl border bg-white"></div>
    ),
  },
  {
    value: "dark",
    label: t("settings.theme.dark.label"),
    description: t("settings.theme.dark.description"),
    icon: MoonIcon,
    illustration: () => (
      <div className="bg-foreground dark:bg-background h-[100px] rounded-xl border"></div>
    ),
  },
  {
    value: "system",
    label: t("settings.theme.system.label"),
    description: t("settings.theme.system.description"),
    icon: MonitorIcon,
    illustration: () => (
      <div className="flex h-[100px] overflow-hidden rounded-xl border">
        <div className="bg-foreground dark:bg-background w-1/2 flex-1">
          <div className="relative size-full"></div>
        </div>
        <div className="w-1/2 flex-1 bg-white"></div>
      </div>
    ),
  },
];

const UNASSIGNED = "unassigned";

export type User = NonNullable<TicketDetail["assignedTo"]>;
export type InternalUser = InternalUsersQuery["internalUsers"][number];

export const mapUserToFilter = (value: string | null) => {
  if (!value) {
    return UNASSIGNED;
  }
  return value;
};

export const mapFilterToUser = (filter: string) => {
  if (filter === UNASSIGNED) {
    return null;
  }
  return filter;
};

export const getNoAssigneeUser = (t: TFunction) => ({
  value: null,
  label: t("tickets.filters.assignee.no_assignee"),
  icon: () => <UserCircleIcon className="size-4" />,
});

export const ASSIGN_MATE_USER = {
  value: "actor_assignMate",
  label: "Assign Mate",
  icon: () => (
    <Avatar className="size-4">
      <AvatarImage
        src="https://api.dicebear.com/9.x/bottts/svg?seed=actor_assignMate"
        alt="Assign Mate"
      />
      <AvatarFallback className="text-xs">A</AvatarFallback>
    </Avatar>
  ),
};

export const mapAssigneeToList = (users: InternalUser[]) => [
  ...users.map((user) => ({
    value: user.id,
    label: `${user.internalUser.firstName} ${user.internalUser.lastName}`,
    icon: () => (
      <Avatar className="size-4">
        <AvatarImage
          src={user.internalUser.picture}
          alt={`${user.internalUser.firstName} ${user.internalUser.lastName}`}
        />
        <AvatarFallback className="text-xs">
          {user.internalUser.firstName.charAt(0)}
        </AvatarFallback>
      </Avatar>
    ),
  })),
];

export const getUserFullName = (user: User) => {
  return user.__typename === "ActorInternalUser"
    ? `${user.internalUser.firstName} ${user.internalUser.lastName}`
    : user.__typename === "ActorBotUser"
      ? user.botUser.name
      : `${user.supplierUser.firstName} ${user.supplierUser.lastName}`;
};

export const filterUsers = (users: InternalUser[], filters: UsersFilters) => {
  const filteredUsers = matchSorter(users, filters.search, {
    keys: [
      "internalUser.firstName",
      "internalUser.lastName",
      "internalUser.email",
    ],
  });

  return filteredUsers;
};

export const sortUsers = (a: InternalUser, b: InternalUser) =>
  sortRole(a.internalUser.supportRole, b.internalUser.supportRole) ||
  a.internalUser.firstName.localeCompare(b.internalUser.firstName) ||
  a.internalUser.lastName.localeCompare(b.internalUser.lastName);

export interface UsersFilters {
  search: string;
}

export function useUsersFilters(
  defaultFilters: UsersFilters = {
    search: "",
  },
) {
  const [filters, setFilters] = useState<UsersFilters>(defaultFilters);

  const clearFilters = () => {
    setFilters({
      search: "",
    });
  };

  return { filters, setFilters, clearFilters } as const;
}
